import React from "react";

/**
 * AidWaive global AI loading spinner.
 *
 * A polished circular ring spinner in the brand blue. Used everywhere
 * the app is waiting on an AI response or any other meaningful network
 * call so the user never sees a frozen UI.
 *
 * Variants:
 *   - inline: small spinner sized to surrounding text (default)
 *   - block:  centered block with optional label, used in card bodies
 *   - overlay: fullscreen translucent overlay with centered spinner
 */
export default function Spinner({
  size = 24,
  variant = "inline",
  label = null,
  className = "",
  testId = "spinner",
}) {
  const ring = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 50 50"
      role="status"
      aria-label={label || "Loading"}
      data-testid={`${testId}-ring`}
      style={{ display: "block" }}
    >
      <circle
        cx="25" cy="25" r="20"
        fill="none"
        stroke="#dbeafe"
        strokeWidth="5"
      />
      <circle
        cx="25" cy="25" r="20"
        fill="none"
        stroke="#1d4ed8"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray="90 150"
        strokeDashoffset="0"
        style={{
          transformOrigin: "center",
          animation: "aw-spin 0.9s linear infinite",
        }}
      />
    </svg>
  );

  if (variant === "overlay") {
    return (
      <div
        className={`fixed inset-0 z-[60] flex items-center justify-center ${className}`}
        style={{ background: "rgba(255,255,255,0.78)", backdropFilter: "blur(2px)" }}
        data-testid={testId}
        role="status"
        aria-live="polite"
      >
        <div className="flex flex-col items-center gap-3">
          {ring}
          {label && (
            <p className="text-sm text-slate-600" data-testid={`${testId}-label`}>{label}</p>
          )}
        </div>
      </div>
    );
  }

  if (variant === "block") {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-3 py-10 ${className}`}
        data-testid={testId}
        role="status"
        aria-live="polite"
      >
        {ring}
        {label && (
          <p className="text-sm text-slate-600" data-testid={`${testId}-label`}>{label}</p>
        )}
      </div>
    );
  }

  // inline
  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      {ring}
      {label && <span className="text-sm text-slate-600">{label}</span>}
    </span>
  );
}
